import { useState } from "react";
import { toast } from "sonner";
import { Plus, X, ArrowUp, ArrowDown, ImageIcon, MousePointerClick } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { DatePicker } from "./DatePicker";
import { api, errMsg } from "../lib/api";

const SLOTS = [
  ["sidebar", "Bawah keranjang"],
  ["catalog_mid", "Tengah katalog"],
  ["footer", "Bawah halaman"],
];
const CHANNELS = [
  ["both", "Toko + Resto"],
  ["grocery", "Toko"],
  ["resto", "Resto"],
];

const empty = {
  image_url: "",
  title: "",
  title_ja: "",
  link: "",
  slot: "sidebar",
  channel: "both",
  start_date: "",
  end_date: "",
  clicks: 0,
  active: true,
};

/** Pengelola slot iklan/poster untuk halaman toko & resto. */
export const PosterCard = ({ s, setS }) => {
  const [busy, setBusy] = useState(-1);
  const posters = s.posters || [];
  const setPosters = (list) => setS({ ...s, posters: list });

  const patch = (i, p) => setPosters(posters.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const move = (i, d) => {
    const list = [...posters];
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    setPosters(list);
  };

  const upload = async (i, file) => {
    if (!file) return;
    setBusy(i);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      patch(i, { image_url: data.url });
    } catch (err) {
      toast.error(errMsg(err, "Gagal mengunggah poster"));
    } finally {
      setBusy(-1);
    }
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4"
      data-testid="poster-card"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="font-display font-semibold">Slot Iklan / Poster</p>
        <Button
          size="sm"
          variant="outline"
          data-testid="add-poster-btn"
          onClick={() => setPosters([...posters, { ...empty }])}
        >
          <Plus size={15} className="mr-1" /> Tambah poster
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Pasang poster promo di ruang kosong halaman: <b>bawah keranjang</b> (tegak 3:4),{" "}
        <b>tengah katalog</b>, atau <b>bawah halaman</b> (melebar — pakai gambar mendatar 16:6 agar
        tulisan tidak terpotong). Bila satu slot punya beberapa poster, tampil bergantian tiap 6
        detik. Kosongkan tanggal bila ingin poster tayang terus.
      </p>
      {posters.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="poster-empty">
          Belum ada poster.
        </p>
      )}
      {posters.map((p, i) => (
        <div
          key={`poster-row-${i}`}
          className="border border-gray-200 rounded-xl p-4 space-y-3"
          data-testid={`poster-row-${i}`}
        >
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Judul (Indonesia)</p>
              <Input
                data-testid={`poster-title-${i}`}
                placeholder="Mis. Gratis antar min. ¥2.000"
                value={p.title || ""}
                onChange={(e) => patch(i, { title: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Judul (Jepang)</p>
              <Input
                data-testid={`poster-title-ja-${i}`}
                placeholder="日本語の見出し"
                value={p.title_ja || ""}
                onChange={(e) => patch(i, { title_ja: e.target.value })}
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <p className="text-xs font-medium text-muted-foreground">
                Link tujuan (opsional) — mis. https://wa.me/... atau /produk/ID
              </p>
              <Input
                data-testid={`poster-link-input-${i}`}
                placeholder="https://wa.me/8180xxxx atau /toko?cat=Bento"
                value={p.link || ""}
                onChange={(e) => patch(i, { link: e.target.value })}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (!v || v.startsWith("/") || /^https?:\/\//i.test(v)) return;
                  patch(i, { link: `https://${v}` });
                }}
              />
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Posisi</p>
              <div className="grid grid-cols-3 gap-2">
                {SLOTS.map(([v, label]) => (
                  <button
                    key={v}
                    data-testid={`poster-slot-${i}-${v}`}
                    onClick={() => patch(i, { slot: v })}
                    className={`py-2 rounded-xl text-xs font-medium border transition-colors duration-200 ${
                      p.slot === v
                        ? "bg-gray-900 text-white border-transparent"
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Tampil di halaman</p>
              <div className="grid grid-cols-3 gap-2">
                {CHANNELS.map(([v, label]) => (
                  <button
                    key={v}
                    data-testid={`poster-channel-${i}-${v}`}
                    onClick={() => patch(i, { channel: v })}
                    className={`py-2 rounded-xl text-xs font-medium border transition-colors duration-200 ${
                      p.channel === v
                        ? "bg-gray-900 text-white border-transparent"
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Tayang mulai (opsional)</p>
              <DatePicker
                testid={`poster-start-${i}`}
                className="h-10 w-full"
                value={p.start_date || ""}
                placeholder="Mulai tayang"
                onChange={(v) => patch(i, { start_date: v })}
              />
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Tayang sampai (opsional)</p>
              <DatePicker
                testid={`poster-end-${i}`}
                className="h-10 w-full"
                value={p.end_date || ""}
                placeholder="Akhir tayang"
                onChange={(v) => patch(i, { end_date: v })}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {p.image_url ? (
              <img src={p.image_url} alt="" className="h-16 w-12 rounded-lg object-cover" />
            ) : (
              <div className="h-16 w-12 rounded-lg bg-gray-100 grid place-items-center text-gray-400">
                <ImageIcon size={16} />
              </div>
            )}
            <label
              className="px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm font-medium cursor-pointer"
              data-testid={`poster-upload-label-${i}`}
            >
              {busy === i ? "Mengunggah…" : "Unggah poster"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                data-testid={`poster-upload-${i}`}
                onChange={(e) => upload(i, e.target.files?.[0])}
              />
            </label>
            <Switch
              data-testid={`poster-active-${i}`}
              checked={p.active !== false}
              onCheckedChange={(v) => patch(i, { active: v })}
            />
            <span className="text-sm text-muted-foreground">Aktif</span>
            <span
              className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700"
              data-testid={`poster-clicks-${i}`}
              title="Jumlah klik poster oleh pembeli"
            >
              <MousePointerClick size={13} /> {p.clicks || 0} klik
            </span>
            <div className="ml-auto flex items-center gap-1">
              <Button
                size="icon"
                variant="ghost"
                data-testid={`poster-up-${i}`}
                onClick={() => move(i, -1)}
              >
                <ArrowUp size={15} />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                data-testid={`poster-down-${i}`}
                onClick={() => move(i, 1)}
              >
                <ArrowDown size={15} />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive"
                data-testid={`poster-delete-${i}`}
                onClick={() => setPosters(posters.filter((_, j) => j !== i))}
              >
                <X size={15} />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
